import { AxiosResponse, CancelTokenSource } from 'axios'

import type { ApiClient } from 'clients/yogi-api'

const PROJECT_LIST_URL = '/project/list'
const PROJECT_DETAILS_URL = '/project'
const PROJECT_DELETE_URL = '/project'
const PROJECT_ID_URL = '/project/uuid'
const PROJECT_SETTINGS = '/project/client-settings'
const CLIENT_PROJECT_CACHE_URL = '/client/project/cache'
const HUB_UUID_HEADER = 'X-Hub-UUID'
const POST_TEXT_URL = '/post/text'
const POST_FEEDBACK_URL = '/post/feedback'
import {
  ClientSettings,
  CreateProjectResponse,
  PostDetails,
  Project,
  ProjectDetails,
} from 'types/project'

export class ProjectService {
  constructor(private client: ApiClient) {}

  async getProjectCacheAvailability(
    proj_uuid: string,
  ): Promise<{ available: boolean }> {
    const response = await this.client.get<{ available: boolean }>(
      CLIENT_PROJECT_CACHE_URL,
      {
        headers: {
          [HUB_UUID_HEADER]: proj_uuid,
        },
      },
    )
    return response.data
  }

  /**
   *
   * @param {string} proj_title - The project title
   * @returns {Promise<CreateProjectResponse>>} Project UUID
   */
  getProjectId(
    proj_title: string,
  ): Promise<AxiosResponse<CreateProjectResponse>> {
    return this.client.get(PROJECT_ID_URL, { params: { proj_title } })
  }

  /**
   * Fetches project details
   * @param {string} proj_uuid - The project id
   * @returns {Promise<ProjectDetails>} List of projects
   */
  async getProject(proj_uuid: string): Promise<ProjectDetails> {
    const { data }: AxiosResponse<ProjectDetails> = await this.client.get(
      PROJECT_DETAILS_URL,
      { params: { proj_uuid } },
    )
    const { data: clientSettings } = await this.client.get<ClientSettings>(
      PROJECT_SETTINGS,
      {
        params: { proj_uuid },
      },
    )

    return { ...data, clientSettings }
  }

  /**
   * Fetches project list
   * @returns {Promise<Project[]>} List of projects
   */
  fetchProjects(): Promise<AxiosResponse<Project[]>> {
    return this.client.get(PROJECT_LIST_URL)
  }

  /**
   * Removes the project
   * @param {string} proj_uuid - The project UUID
   * @returns nothing
   */
  removeProject(proj_uuid: string): Promise<AxiosResponse<void>> {
    return this.client.delete(PROJECT_DELETE_URL, { params: { proj_uuid } })
  }

  /**
   * Fetches node details
   * @param {string} post_uuid - The node UUID
   * @param {boolean} fragment - Flag to show specific data
   * @param {string} proj_uuid - The project UUID (optional for backward compatibility)
   * @param {CancelTokenSource} cancelToken - Axios token to cancel the request
   * @returns {Promise<PostDetails>} Node details
   */
  async getPostDetails(
    post_uuid: string,
    _fragment: boolean,
    proj_uuid?: string,
    cancelToken?: CancelTokenSource,
  ): Promise<AxiosResponse<PostDetails>> {
    const response = await this.client.post<PostDetails>(
      '/client/feedback/post',
      {
        post_uuid,
      },
      {
        params: proj_uuid ? { proj_uuid } : {},
        cancelToken: cancelToken?.token,
      },
    )

    // Transform the response to match PostDetails structure
    const transformedData: PostDetails = {
      ...response.data,
      node_size: 0, // Default value since new endpoint doesn't provide this
      userid: null, // Default value since new endpoint doesn't provide this
      metadata: '', // Default value since new endpoint doesn't provide this
      sentiment: response.data.sentiment,
      editable_fields: (() => {
        const raw: unknown = response.data.editable_fields
        if (typeof raw === 'string') {
          return raw.split(',').reduce(
            (acc: Record<string, boolean>, field: string) => ({
              ...acc,
              [field.trim()]: true,
            }),
            {},
          )
        }
        if (Array.isArray(raw)) {
          return (raw as string[]).reduce(
            (acc: Record<string, boolean>, field: string) => ({
              ...acc,
              [field]: true,
            }),
            {},
          )
        }
        return (raw as Record<string, boolean>) || {}
      })(),
    }

    return {
      ...response,
      data: transformedData,
    }
  }

  /**
   * Fetches node text
   * @param {string} post_uuid - The node UUID
   * @param {CancelTokenSource} cancelToken - Axios token to cancel the request
   * @returns {Promise<string>} Node text
   */
  getPostText(
    post_uuid: string,
    cancelToken?: CancelTokenSource,
  ): Promise<AxiosResponse<string>> {
    return this.client.get(POST_TEXT_URL, {
      params: { post_uuid },
      cancelToken: cancelToken?.token,
    })
  }

  /**
   * Send feedback about a specific post
   * @param {string} post_uuid - The node UUID
   * @param proj_uuid
   * @param fragment
   * @param feedback
   * @returns nothing
   */
  sendFeedbackText(
    post_uuid: string,
    proj_uuid: string,
    fragment: boolean,
    feedback: string | undefined,
  ): Promise<AxiosResponse<void>> {
    return this.client.put(
      POST_FEEDBACK_URL,
      { feedback: feedback },
      { params: { post_uuid, proj_uuid, fragment } },
    )
  }
}
